import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, editionsOf, errorMessage, type Edition, type Feature, type Product, type ProductDetail } from '../lib/api';
import { useAsync } from '../lib/hooks';
import { fmtDate } from '../lib/format';
import { ActionButton, ErrorAlert, Field, Loading, Modal, PageHeader, Spinner } from '../components/ui';
import { useT } from '../i18n';

export function ProductsPage() {
  const t = useT();
  const list = useAsync(() => api.get<Product[]>('/v1/admin/products'), []);
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHeader
        title={t('products.title')}
        subtitle={t('products.subtitle')}
        actions={<button className="btn btn-primary" onClick={() => setCreating(true)}>{t('products.new')}</button>}
      />
      <ErrorAlert error={list.error} />
      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('common.product')}</th>
                <th>{t('products.colCode')}</th>
                <th>{t('products.colGrace')}</th>
                <th>{t('products.colRefresh')}</th>
                <th>{t('products.colTrial')}</th>
                <th>{t('common.created')}</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.length === 0 && <tr><td colSpan={6} className="empty">{t('products.empty')}</td></tr>}
              {list.data?.map((p) => (
                <tr key={p.id}>
                  <td><Link to={`/productos/${p.id}`}>{p.name}</Link></td>
                  <td><code>{p.code}</code></td>
                  <td>{t('common.days', { n: p.offline_grace_days })}</td>
                  <td>{t('products.everyHours', { n: p.refresh_interval_hours })}</td>
                  <td>{p.trial_enabled ? t('common.days', { n: p.trial_days }) : t('products.no')}</td>
                  <td>{fmtDate(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {creating && <ProductModal onClose={() => setCreating(false)} />}
    </>
  );
}

function ProductModal({ product, onClose, onSaved }: { product?: Product; onClose: () => void; onSaved?: () => Promise<void> }) {
  const t = useT();
  const navigate = useNavigate();
  const [f, setF] = useState({
    code: product?.code ?? '',
    name: product?.name ?? '',
    description: product?.description ?? '',
    offline_grace_days: product?.offline_grace_days ?? 7,
    refresh_interval_hours: product?.refresh_interval_hours ?? 24,
    floating_lease_minutes: product?.floating_lease_minutes ?? 15,
    offline_activation_days: product?.offline_activation_days ?? 365,
    trial_enabled: product?.trial_enabled ?? true,
    trial_days: product?.trial_days ?? 14,
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setF({ ...f, [k]: e.target.type === 'number' ? Number(e.target.value) : e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = { ...f, description: f.description || null };
      if (product) {
        await api.patch(`/v1/admin/products/${product.id}`, body);
        await onSaved?.();
      } else {
        const p = await api.post<Product>('/v1/admin/products', body);
        navigate(`/productos/${p.id}`);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={product ? t('products.editTitle') : t('products.newTitle')} onClose={onClose}>
      <ErrorAlert error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label={t('common.name')}><input required value={f.name} onChange={set('name')} /></Field>
          <Field label={t('products.code')} help={t('products.codeHelp')}>
            <input required pattern="[a-z0-9][a-z0-9._\-]{1,63}" value={f.code} onChange={set('code')} />
          </Field>
          <Field label={t('common.description')} full><textarea value={f.description} onChange={set('description')} /></Field>
          <Field label={t('products.graceDays')} help={t('products.graceHelp')}>
            <input type="number" min={0} value={f.offline_grace_days} onChange={set('offline_grace_days')} />
          </Field>
          <Field label={t('products.refreshHours')}><input type="number" min={1} value={f.refresh_interval_hours} onChange={set('refresh_interval_hours')} /></Field>
          <Field label={t('products.leaseMinutes')} help={t('products.leaseHelp')}>
            <input type="number" min={1} value={f.floating_lease_minutes} onChange={set('floating_lease_minutes')} />
          </Field>
          <Field label={t('products.offlineDays')}><input type="number" min={1} value={f.offline_activation_days} onChange={set('offline_activation_days')} /></Field>
          <label className="checks"><label><input type="checkbox" checked={f.trial_enabled} onChange={set('trial_enabled')} /> {t('products.allowTrial')}</label></label>
          <Field label={t('products.trialDays')}><input type="number" min={0} value={f.trial_days} onChange={set('trial_days')} /></Field>
        </div>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={busy}>{busy && <Spinner />} {t('common.save')}</button>
        </div>
      </form>
    </Modal>
  );
}

export function ProductDetailPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const p = useAsync(() => api.get<ProductDetail>(`/v1/admin/products/${id}`), [id]);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [feat, setFeat] = useState({ code: '', name: '' });
  const [ver, setVer] = useState({ version: '', released_at: '' });
  const [edition, setEdition] = useState<Edition | 'new' | null>(null);
  const [feature, setFeature] = useState<Feature | null>(null);
  if (!p.data) return p.error ? <ErrorAlert error={p.error} /> : <Loading />;
  const product = p.data;
  const editions = editionsOf(product);
  // La más reciente arriba: con la lista limitada en altura es la que interesa ver sin desplazarse.
  const versions = [...product.versions].sort((a, b) => b.released_at.localeCompare(a.released_at));
  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await p.reload();
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const toggleList = (field: 'trial_features' | 'default_features', code: string, on: boolean) =>
    run(() =>
      api.patch(`/v1/admin/products/${product.id}`, {
        [field]: on ? [...product[field], code] : product[field].filter((c) => c !== code),
      }),
    );

  return (
    <>
      <PageHeader
        crumb={<Link to="/productos">{t('products.back')}</Link>}
        title={product.name}
        subtitle={<><code>{product.code}</code> · {t('products.licenseCount', { n: product.license_count })}</>}
        actions={
          <>
            <Link className="btn" to={`/licencias?product_id=${product.id}`}>{t('products.viewLicenses')}</Link>
            <button className="btn" onClick={() => setEditing(true)}>{t('common.edit')}</button>
            {product.license_count === 0 && (
              <ActionButton
                className="btn btn-danger"
                confirm={t('products.deleteConfirm')}
                onAction={() => run(async () => { await api.del(`/v1/admin/products/${product.id}`); window.location.assign('/productos'); })}
              >
                {t('common.delete')}
              </ActionButton>
            )}
          </>
        }
      />
      <ErrorAlert error={error} />
      <div className="grid grid-2">
        <section className="card">
          <h2>{t('products.config')}</h2>
          <dl className="props">
            <dt>{t('products.grace')}</dt><dd>{t('common.days', { n: product.offline_grace_days })}</dd>
            <dt>{t('products.refresh')}</dt><dd>{t('products.refreshEvery', { n: product.refresh_interval_hours })}</dd>
            <dt>{t('products.lease')}</dt><dd>{t('products.leaseValue', { n: product.floating_lease_minutes })}</dd>
            <dt>{t('products.offline')}</dt><dd>{t('products.offlineValue', { n: product.offline_activation_days })}</dd>
            <dt>{t('products.trial')}</dt><dd>{product.trial_enabled ? t('products.trialValue', { n: product.trial_days }) : t('products.disabled')}</dd>
            <dt>{t('common.description')}</dt><dd>{product.description ?? '—'}</dd>
          </dl>
        </section>
        <section className="card">
          <h2>{t('products.versions')} <span className="muted">({versions.length})</span></h2>
          <p className="muted" style={{ marginTop: 0 }}>{t('products.versionsHelp')}</p>
          <div className="table-wrap scroll-y" data-testid="versions">
            <table>
              <thead><tr><th>{t('products.colVersion')}</th><th>{t('products.colPublished')}</th><th /></tr></thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.id}>
                    <td><code>{v.version}</code></td>
                    <td>{fmtDate(v.released_at)}</td>
                    <td className="num">
                      <ActionButton className="btn btn-sm btn-danger" confirm={t('products.deleteVersionConfirm', { v: v.version })} onAction={() => run(() => api.del(`/v1/admin/product-versions/${v.id}`))}>
                        {t('common.delete')}
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <form
            className="toolbar"
            style={{ marginTop: 12, marginBottom: 0 }}
            onSubmit={(e) => {
              e.preventDefault();
              void run(async () => {
                await api.post(`/v1/admin/products/${product.id}/versions`, { version: ver.version, released_at: ver.released_at ? new Date(ver.released_at).toISOString() : undefined });
                setVer({ version: '', released_at: '' });
              });
            }}
          >
            <input placeholder="2.1.0" required value={ver.version} onChange={(e) => setVer({ ...ver, version: e.target.value })} />
            <input type="date" value={ver.released_at} onChange={(e) => setVer({ ...ver, released_at: e.target.value })} aria-label={t('products.publishDate')} />
            <button className="btn">{t('products.addVersion')}</button>
          </form>
        </section>
      </div>

      <section className="card" style={{ marginTop: 16 }}>
        <h2>{t('products.features')}</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('products.colCode')}</th>
                <th>{t('products.colName')}</th>
                <th>{t('products.colDefault')}</th>
                <th>{t('products.colInTrial')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {product.features.length === 0 && <tr><td colSpan={5} className="empty">{t('products.noFeatures')}</td></tr>}
              {product.features.map((f) => (
                <tr key={f.id}>
                  <td><code>{f.code}</code></td>
                  <td>
                    {f.name}
                    {f.description && <div className="muted" style={{ fontSize: 12 }}>{f.description}</div>}
                  </td>
                  <td><input type="checkbox" aria-label={t('products.colDefault')} checked={product.default_features.includes(f.code)} onChange={(e) => void toggleList('default_features', f.code, e.target.checked)} /></td>
                  <td><input type="checkbox" aria-label={t('products.colInTrial')} checked={product.trial_features.includes(f.code)} onChange={(e) => void toggleList('trial_features', f.code, e.target.checked)} /></td>
                  <td className="num">
                    <button className="btn btn-sm" onClick={() => setFeature(f)}>{t('common.edit')}</button>{' '}
                    <ActionButton className="btn btn-sm btn-danger" confirm={t('products.deleteFeatureConfirm', { code: f.code })} onAction={() => run(() => api.del(`/v1/admin/features/${f.id}`))}>
                      {t('common.delete')}
                    </ActionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form
          className="toolbar"
          style={{ marginTop: 12, marginBottom: 0 }}
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              await api.post(`/v1/admin/products/${product.id}/features`, feat);
              setFeat({ code: '', name: '' });
            });
          }}
        >
          <input placeholder={t('products.featureCodePlaceholder')} required pattern="[a-z0-9][a-z0-9._\-]{0,63}" value={feat.code} onChange={(e) => setFeat({ ...feat, code: e.target.value })} />
          <input className="grow" placeholder={t('products.featureNamePlaceholder')} required value={feat.name} onChange={(e) => setFeat({ ...feat, name: e.target.value })} />
          <button className="btn">{t('products.addFeature')}</button>
        </form>
      </section>
      <section className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <h2>{t('products.editions')}</h2>
          <button className="btn btn-sm" onClick={() => setEdition('new')}>{t('products.newEdition')}</button>
        </div>
        <p className="muted" style={{ marginTop: 0 }}>{t('products.editionsHelp')}</p>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('products.colName')}</th>
                <th>{t('products.colCode')}</th>
                <th>{t('products.features')}</th>
                <th>{t('products.colLimits')}</th>
                <th className="num">{t('products.colLicenses')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {editions.length === 0 && <tr><td colSpan={6} className="empty">{t('products.noEditions')}</td></tr>}
              {editions.map((e) => (
                <tr key={e.id}>
                  <td>{e.name}</td>
                  <td><code>{e.code}</code></td>
                  <td>{e.features.length ? e.features.map((f) => <span key={f} className="tag">{f}</span>) : <span className="muted">{t('common.noneF')}</span>}</td>
                  <td>
                    {Object.keys(e.limits).length
                      ? Object.entries(e.limits).map(([k, v]) => <span key={k} className="tag">{k}={v}</span>)
                      : <span className="muted">{t('common.none')}</span>}
                  </td>
                  <td className="num">
                    {e.license_count ? <Link to={`/licencias?product_id=${product.id}&edition_id=${e.id}`}>{e.license_count}</Link> : 0}
                  </td>
                  <td className="num">
                    <button className="btn btn-sm" onClick={() => setEdition(e)}>{t('common.edit')}</button>{' '}
                    {!e.license_count && (
                      <ActionButton className="btn btn-sm btn-danger" confirm={t('products.deleteEditionConfirm', { name: e.name })} onAction={() => run(() => api.del(`/v1/admin/editions/${e.id}`))}>
                        {t('common.delete')}
                      </ActionButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {editing && <ProductModal product={product} onClose={() => setEditing(false)} onSaved={async () => { setEditing(false); await p.reload(); }} />}
      {edition && (
        <EditionModal
          product={product}
          edition={edition === 'new' ? undefined : edition}
          onClose={() => setEdition(null)}
          onSaved={async () => { setEdition(null); await p.reload(); }}
        />
      )}
      {feature && <FeatureModal feature={feature} onClose={() => setFeature(null)} onSaved={async () => { setFeature(null); await p.reload(); }} />}
    </>
  );
}

/** El código no se edita: es lo que comprueban las apps y lo que guardan licencias y ediciones. */
function FeatureModal({ feature, onClose, onSaved }: { feature: Feature; onClose: () => void; onSaved: () => Promise<void> }) {
  const t = useT();
  const [name, setName] = useState(feature.name);
  const [description, setDescription] = useState(feature.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/v1/admin/features/${feature.id}`, { name, description: description || null });
      await onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={t('products.editFeatureTitle')} onClose={onClose}>
      <ErrorAlert error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label={t('products.code')} help={t('products.featureCodeHelp')}>
            <input value={feature.code} disabled />
          </Field>
          <Field label={t('common.name')}>
            <input required value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('common.description')} full>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </div>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={busy}>{busy && <Spinner />} {t('common.save')}</button>
        </div>
      </form>
    </Modal>
  );
}

function EditionModal({ product, edition, onClose, onSaved }: { product: ProductDetail; edition?: Edition; onClose: () => void; onSaved: () => Promise<void> }) {
  const t = useT();
  const [f, setF] = useState({
    code: edition?.code ?? '',
    name: edition?.name ?? '',
    description: edition?.description ?? '',
    sort_order: edition?.sort_order ?? (editionsOf(product).at(-1)?.sort_order ?? 0) + 10,
  });
  const [features, setFeatures] = useState<string[]>(edition?.features ?? []);
  const [limits, setLimits] = useState(JSON.stringify(edition?.limits ?? {}, null, 2));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      let parsed: Record<string, number>;
      try {
        parsed = JSON.parse(limits || '{}') as Record<string, number>;
      } catch {
        throw new Error(t('licenseDetail.limitsInvalid'));
      }
      const body = { name: f.name, description: f.description || null, sort_order: f.sort_order, features, limits: parsed };
      if (edition) await api.patch(`/v1/admin/editions/${edition.id}`, body);
      else await api.post(`/v1/admin/products/${product.id}/editions`, { ...body, code: f.code });
      await onSaved();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={edition ? t('products.editEditionTitle') : t('products.newEditionTitle')} onClose={onClose}>
      <ErrorAlert error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label={t('common.name')}><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Pro" /></Field>
          <Field label={t('products.code')} help={t('products.editionCodeHelp')}>
            <input required disabled={!!edition} pattern="[a-z0-9][a-z0-9._\-]{0,63}" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="pro" />
          </Field>
          <Field label={t('common.description')} full><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <Field label={t('products.features')} full>
            <div className="checks">
              {product.features.map((x) => (
                <label key={x.id}>
                  <input type="checkbox" checked={features.includes(x.code)} onChange={(e) => setFeatures(e.target.checked ? [...features, x.code] : features.filter((c) => c !== x.code))} />
                  {x.name} <code className="muted">{x.code}</code>
                </label>
              ))}
              {product.features.length === 0 && <span className="muted">{t('createLicense.noFeatures')}</span>}
            </div>
          </Field>
          <Field label={t('licenseDetail.limitsJson')}>
            <textarea className="mono" value={limits} onChange={(e) => setLimits(e.target.value)} />
          </Field>
          <Field label={t('products.order')} help={t('products.orderHelp')}>
            <input type="number" min={0} value={f.sort_order} onChange={(e) => setF({ ...f, sort_order: Number(e.target.value) })} />
          </Field>
        </div>
        <p className="muted">{t('products.editionChangesNote')}</p>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={busy}>{busy && <Spinner />} {t('common.save')}</button>
        </div>
      </form>
    </Modal>
  );
}
